import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import * as yup from 'yup';
import type { CreateClientFormData } from '@/types/clientsApiTypes';
import {
  createClientSchema,
  PRIMARY_PASSENGER_NAME_KEYS,
  RELATED_PASSENGER_NAME_KEYS,
  REQUIRED_FORM_KEYS,
  REQUIRED_RELATED_PASSENGER_KEYS,
} from '../../utils/validationSchema';

const getValueByPath = (path: string, root: unknown): unknown =>
  path
    .split('.')
    .reduce<unknown>((value, key) => (value == null ? undefined : (value as Record<string, unknown>)[key]), root);

const isFieldValid = (path: string, root: unknown): boolean => {
  const fieldSchema = yup.reach(createClientSchema, path);
  if (!('isValidSync' in fieldSchema)) return true;
  return (fieldSchema as yup.Schema).isValidSync(getValueByPath(path, root));
};

const areFieldsValid = (paths: readonly string[], root: unknown): boolean =>
  paths.every((path) => isFieldValid(path, root));

const buildRelatedPassengerPaths = (count: number, keys: readonly string[]): string[] =>
  Array.from({ length: count }, (_, index) => keys.map((key) => `related_passengers.${index}.${key}`)).flat();

export const useGetIsPassengerValid = () => {
  const { watch } = useFormContext<CreateClientFormData>();
  const formData = watch();
  const relatedPassengers = watch('related_passengers');

  const isPrimaryPassengerValid = useMemo(() => areFieldsValid(REQUIRED_FORM_KEYS, formData), [formData]);

  const isRelatedPassengersValid = useMemo(() => {
    if (!relatedPassengers || relatedPassengers.length === 0) {
      return false;
    }
    return areFieldsValid(buildRelatedPassengerPaths(relatedPassengers.length, REQUIRED_RELATED_PASSENGER_KEYS), formData);
  }, [relatedPassengers, formData]);

  const arePrimaryNamesWithinLimit = useMemo(() => areFieldsValid(PRIMARY_PASSENGER_NAME_KEYS, formData), [formData]);

  const areRelatedNamesWithinLimit = useMemo(
    () => areFieldsValid(buildRelatedPassengerPaths(relatedPassengers?.length ?? 0, RELATED_PASSENGER_NAME_KEYS), formData),
    [formData, relatedPassengers]
  );

  return {
    isRelatedPassengersValid,
    isPrimaryPassengerValid,
    arePrimaryNamesWithinLimit,
    areRelatedNamesWithinLimit,
  };
};
